import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext.jsx';
import { AnimatedBackground } from '../components/AnimatedBackground.jsx';
import { OminiaLogo } from '../components/OminiaLogo.jsx';
import { GOLD_MONTHLY_BRL, PLAN_COPY } from '../config/pricing.js';
import { fetchPublicPlanCatalog } from '../billing/publicPlans.js';
import { LandingPreviewSection } from '../components/LandingPreviewSection.jsx';

const CHECK = (
  <svg className="h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
    <path
      fillRule="evenodd"
      d="M16.704 5.29a1 1 0 010 1.42l-7.995 8a1 1 0 01-1.414 0l-4.004-4a1 1 0 111.414-1.414l3.297 3.29 7.294-7.29a1 1 0 011.413 0z"
      clipRule="evenodd"
    />
  </svg>
);

export function Home() {
  const { scheme, toggle } = useTheme();
  const [planCopy, setPlanCopy] = useState(() => ({
    trial: { ...PLAN_COPY.trial, bullets: [...PLAN_COPY.trial.bullets] },
    gold: { ...PLAN_COPY.gold, bullets: [...PLAN_COPY.gold.bullets] },
  }));
  const [goldMonthlyBrl, setGoldMonthlyBrl] = useState(GOLD_MONTHLY_BRL);

  useEffect(() => {
    let cancelled = false;
    fetchPublicPlanCatalog().then((cat) => {
      if (cancelled) return;
      setPlanCopy(cat.copy);
      setGoldMonthlyBrl(cat.goldMonthlyBrl);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const tc = planCopy.trial;
  const gc = planCopy.gold;

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <AnimatedBackground />

      <div className="relative z-10">
        <header className="sticky top-0 z-30 border-b border-zinc-200/80 bg-zinc-50/90 backdrop-blur-xl dark:border-white/10 dark:bg-zinc-950/95 dark:shadow-[inset_0_-1px_0_rgba(255,255,255,0.06)]">
          <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-4">
            <Link to="/" className="flex items-center gap-3 text-zinc-900 dark:text-zinc-100">
              <img
                src="/logo-ominia-hub-ring.svg"
                alt=""
                width={44}
                height={44}
                className="h-11 w-11 drop-shadow-md"
                style={{ animation: 'ominia-spin-slow 18s linear infinite' }}
              />
              <span className="font-display text-lg font-bold tracking-tight">
                <span className="text-gradient">Omin</span>
                <span className="text-gradient-ia">IA</span>
              </span>
            </Link>
            <nav className="ml-auto flex flex-wrap items-center gap-2 sm:gap-3">
              <a
                href="#planos"
                className="rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-600 transition-colors hover:bg-zinc-900/5 dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-zinc-100"
              >
                Planos
              </a>
              <a
                href="#como"
                className="hidden rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-600 transition-colors hover:bg-zinc-900/5 sm:block dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-zinc-100"
              >
                Como funciona
              </a>
              <a
                href="#preview"
                className="rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-600 transition-colors hover:bg-zinc-900/5 dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-zinc-100"
              >
                Demonstração
              </a>
              <Link
                to="/login"
                className="rounded-full border border-zinc-300 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-800 transition-colors hover:border-zinc-400 hover:bg-zinc-100 dark:border-white/15 dark:text-zinc-100 dark:hover:border-white/30 dark:hover:bg-white/10"
              >
                Entrar
              </Link>
              <Link
                to="/cadastro?plan=essential_trial"
                className="rounded-full bg-accent px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-lg shadow-accent/35 transition-transform hover:-translate-y-0.5 hover:shadow-accent/45 active:translate-y-0"
              >
                Testar grátis
              </Link>
              <button
                type="button"
                onClick={toggle}
                className="rounded-full border border-zinc-200 bg-white/80 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-700 transition-colors hover:bg-white dark:border-white/10 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700 dark:hover:text-white"
              >
                {scheme === 'dark' ? 'Claro' : 'Escuro'}
              </button>
            </nav>
          </div>
        </header>

        <section className="mx-auto grid max-w-6xl gap-12 px-4 pb-28 pt-16 lg:grid-cols-[1.08fr_0.92fr] lg:gap-16 lg:pt-24">
          <div className="flex flex-col justify-center gap-9 text-zinc-900 dark:text-zinc-50">
            <div className="inline-flex flex-wrap items-center gap-4">
              <OminiaLogo size={92} />
              <div className="flex flex-wrap items-center gap-2 rounded-full border border-emerald-600/25 bg-emerald-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-900 shadow-sm dark:border-emerald-400/35 dark:bg-emerald-950/55 dark:text-emerald-100 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.07)]">
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-600 shadow-[0_0_10px_rgba(5,150,105,0.85)] dark:bg-emerald-400 dark:shadow-[0_0_12px_rgba(52,211,153,0.55)]" aria-hidden />
                {tc.priceLabel}
                {tc.afterPriceLabel ? ` · ${tc.afterPriceLabel}` : ''}
                {' · sem cartão para começar'}
              </div>
            </div>
            <h1 className="font-display text-4xl font-bold leading-[1.12] tracking-tight sm:text-5xl lg:text-6xl">
              <span className="text-gradient">Omin</span>
              <span className="text-gradient-ia">IA</span>{' '}
              <span className="block pt-2 text-balance text-zinc-900 sm:inline sm:pt-0 dark:text-zinc-50">
                coloca sua loja para receber pedidos pela internet com calma — e cresce junto com você.
              </span>
            </h1>
            <p className="max-w-xl text-lg leading-relaxed text-zinc-600 dark:text-zinc-400">
              Catálogo, carrinho e um painel onde você vê cada pedido em tempo real, com uma loja separada para cada
              negócio. Quando quiser encaixar tudo no sistema que você já usa no escritório, o{' '}
              <strong className="font-semibold text-zinc-900 dark:text-white">Plano Ouro</strong> abre essa ponte.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/cadastro?plan=essential_trial"
                className="rounded-2xl bg-accent px-8 py-4 text-center text-sm font-semibold uppercase tracking-[0.12em] text-white shadow-xl shadow-accent/40 transition hover:-translate-y-0.5"
              >
                Começar teste completo
              </Link>
              <Link
                to="/cadastro?plan=gold"
                className="rounded-2xl border-2 border-amber-500/65 bg-white/55 px-8 py-4 text-center text-sm font-semibold uppercase tracking-[0.1em] text-amber-900 backdrop-blur transition-all hover:-translate-y-0.5 hover:shadow-glow-amber dark:border-amber-400/40 dark:bg-night-elevated/90 dark:text-amber-100 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
              >
                Contratar ouro · R$ {goldMonthlyBrl}/mês
              </Link>
              <Link
                to="/lojateste"
                className="rounded-2xl border border-zinc-200 bg-white px-8 py-4 text-center text-sm font-semibold text-zinc-800 shadow-sm transition-all hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-md dark:border-white/10 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:border-emerald-500/40 dark:hover:bg-zinc-800"
              >
                Ver loja de demonstração
              </Link>
            </div>
            <ul className="flex flex-wrap gap-x-10 gap-y-3 text-sm text-zinc-600 dark:text-zinc-400">
              <li className="flex items-center gap-2">{CHECK} Cada negócio com sua própria loja e dados à parte</li>
              <li className="flex items-center gap-2">{CHECK} Modo claro e escuro na loja e no painel</li>
              <li className="flex items-center gap-2">{CHECK} Pensado para hospedagem simples (site em pasta pública)</li>
            </ul>
          </div>

          <div className="relative rounded-[2rem] border border-zinc-200/80 bg-white/90 p-6 shadow-xl shadow-zinc-900/10 ring-1 ring-black/5 backdrop-blur-xl dark:border-white/10 dark:bg-zinc-900/90 dark:shadow-[0_24px_60px_rgba(0,0,0,0.45)] dark:ring-white/10">
            <img
              src="/logo-ominia-hub-ring.svg"
              alt="Identidade visual OminIA Hub"
              className="pointer-events-none absolute -right-6 -top-6 h-32 w-32 opacity-[0.1] dark:opacity-[0.14]"
            />
            <h2 id="como" className="font-display text-2xl font-semibold text-zinc-900 dark:text-zinc-50">
              Do cadastro ao primeiro pedido
            </h2>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              Passe o mouse nos cartões para ver o resumo — pensado para quem está na loja ou no escritório.
            </p>
            <ol className="mt-6 space-y-4">
              {[
                {
                  t: '1. Conta e plano',
                  d: 'Abra sua conta: sete dias com tudo liberado para experimentar, ou já entre no Ouro se quiser integração com seu sistema.',
                },
                {
                  t: '2. Sua empresa e o endereço da loja',
                  d: 'No painel você cadastra o negócio e escolhe um nome curto para o link — por exemplo, “minhapadaria” vira o endereço da loja na web.',
                },
                {
                  t: '3. Pedidos chegando no painel',
                  d: 'Os pedidos aparecem na hora para você separar e atender; depois é só conectar a forma de pagamento que preferir.',
                },
              ].map((step, i) => (
                <li
                  key={step.t}
                  style={{ animationDelay: `${i * 40}ms` }}
                  className="group/step flex cursor-pointer gap-4 rounded-2xl border border-zinc-100 bg-zinc-50/80 px-4 py-3.5 shadow-sm outline-none transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-emerald-300/60 hover:bg-white hover:shadow-md focus-visible:ring-2 focus-visible:ring-emerald-500/50 active:scale-[0.99] dark:border-zinc-700/70 dark:bg-zinc-800/60 dark:hover:border-emerald-500/45 dark:hover:bg-zinc-800 dark:hover:shadow-[0_12px_40px_rgba(0,0,0,0.35)] dark:focus-visible:ring-emerald-400/55"
                  tabIndex={0}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-lg font-bold text-emerald-800 transition-all duration-300 group-hover/step:scale-105 group-hover/step:bg-emerald-200 group-hover/step:shadow-inner dark:bg-emerald-950/80 dark:text-emerald-300 dark:group-hover/step:bg-emerald-900/90 dark:group-hover/step:shadow-[0_0_20px_rgba(16,185,129,0.2)]">
                    {step.t.slice(0, 1)}
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-zinc-900 transition-colors group-hover/step:text-emerald-800 dark:text-zinc-100 dark:group-hover/step:text-emerald-200">
                      {step.t.slice(3)}
                    </div>
                    <p className="mt-1 text-sm leading-snug text-zinc-600 dark:text-zinc-400">{step.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <LandingPreviewSection />

        <section
          id="planos"
          className="scroll-mt-28 border-y border-ink/5 bg-white/40 px-4 py-24 backdrop-blur dark:border-white/[0.05] dark:bg-gradient-to-b dark:from-night-deep dark:via-night-surface dark:to-night-pitch"
        >
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto mb-14 max-w-2xl text-center">
              <h2 className="font-display text-3xl font-bold sm:text-4xl dark:text-frost">
                Experimente com calma · evolua quando fizer sentido
              </h2>
              <p className="mt-3 text-ink/65 dark:text-frost-muted">
                Nos primeiros dias você usa tudo o que precisa para vender pela internet. A conexão direta com o sistema da
                empresa (estoque, financeiro, etc.) fica no plano pago — sem surpresa na letra miúda.
              </p>
            </div>
            <div className="grid gap-8 md:grid-cols-2 md:items-stretch">
              <article
                className="group/plan relative flex flex-col overflow-hidden rounded-3xl border border-ink/10 bg-mist/90 p-8 shadow-xl transition-all duration-500 ease-out-expo will-change-transform hover:-translate-y-2 hover:border-emerald-400/30 hover:shadow-glow dark:border-white/[0.08] dark:bg-night-elevated/95 dark:shadow-night-card dark:hover:border-emerald-400/35 dark:hover:shadow-night-card-hover"
                tabIndex={0}
              >
                <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-emerald-400/15 opacity-0 blur-3xl transition-opacity duration-500 group-hover/plan:opacity-100 group-focus-within/plan:opacity-100 dark:bg-teal-400/20" />
                <p className="text-xs font-bold uppercase tracking-[0.35em] text-ink/50 dark:text-frost-muted">{tc.name}</p>
                <p className="mt-3 font-display text-3xl font-bold text-ink dark:text-frost">{tc.priceLabel}</p>
                {tc.afterPriceLabel ? (
                  <p className="mt-1 text-sm font-semibold text-ink/70 dark:text-frost-muted">{tc.afterPriceLabel}</p>
                ) : null}
                <p className="text-sm font-medium text-ink/60 dark:text-frost-muted">{tc.subtitle}</p>
                <ul className="relative mt-8 flex-1 space-y-3">
                  {tc.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex gap-3 text-sm text-ink/80 transition-transform duration-300 group-hover/plan:translate-x-0.5 dark:text-frost/90"
                    >
                      <span className="text-accent transition-transform duration-300 group-hover/plan:scale-110 dark:text-teal-300">
                        {CHECK}
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-xs font-medium italic text-accent2 opacity-95 dark:text-orange-300/90">{tc.caveat}</p>
                <Link
                  className="relative mt-8 rounded-2xl bg-ink px-6 py-3 text-center text-sm font-semibold text-mist transition-all hover:-translate-y-0.5 hover:bg-ink/90 hover:shadow-lg active:translate-y-0 dark:bg-frost dark:text-night-deep dark:hover:bg-white dark:hover:shadow-glow"
                  to="/cadastro?plan=essential_trial"
                >
                  Começar agora · sem pagamento inicial
                </Link>
              </article>

              <article
                className="group/gold relative flex flex-col overflow-hidden rounded-3xl border-2 border-amber-500/65 bg-gradient-to-br from-[#2a2318]/95 via-[#1a2630]/95 to-[#0f1a26]/96 p-[1px] shadow-2xl transition-all duration-500 ease-out-expo will-change-transform hover:-translate-y-2 hover:shadow-glow-amber dark:border-amber-400/45 dark:from-amber-950/50 dark:via-night-elevated dark:to-night-deep"
                tabIndex={0}
              >
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-amber-400/10 via-transparent to-teal-500/5 opacity-0 transition-opacity duration-500 group-hover/gold:opacity-100" />
                <div className="relative flex flex-1 flex-col rounded-[22px] bg-mist px-8 py-10 dark:bg-night-elevated/95 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                  <span className="absolute right-6 top-6 rounded-full bg-amber-500 px-4 py-1 text-[11px] font-bold uppercase tracking-widest text-ink shadow-md transition-transform duration-300 group-hover/gold:scale-105 dark:bg-amber-400 dark:text-night-deep">
                    Integração com seu sistema
                  </span>
                  <p className="text-xs font-bold uppercase tracking-[0.35em] text-amber-800 dark:text-amber-300">{gc.name}</p>
                  <p className="mt-4 font-display text-4xl font-bold text-gradient">
                    {gc.priceLabel}
                    <span className="font-sans text-base font-semibold uppercase tracking-normal text-ink dark:text-frost">
                      {gc.cadence}
                    </span>
                  </p>
                  <p className="mt-2 text-sm text-ink/70 dark:text-frost-muted">{gc.subtitle}</p>
                  <ul className="relative mt-8 flex-1 space-y-3">
                    {gc.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex gap-3 text-sm text-ink/85 transition-transform duration-300 group-hover/gold:translate-x-0.5 dark:text-frost/95"
                      >
                        <span className="text-accent transition-transform duration-300 group-hover/gold:scale-110 dark:text-teal-300">
                          {CHECK}
                        </span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    className="relative mt-10 overflow-hidden rounded-2xl bg-gradient-to-r from-accent to-emerald-600 px-6 py-3 text-center text-sm font-semibold uppercase tracking-wide text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-glow active:translate-y-0"
                    to="/cadastro?plan=gold"
                  >
                    <span className="relative z-10">Assinar ouro · ativa logo</span>
                    <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover/gold:translate-x-full" />
                  </Link>
                </div>
              </article>
            </div>
          </div>
        </section>

        <footer className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-20 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
          <div className="flex justify-center gap-3 sm:justify-start">
            <img src="/favicon.svg" alt="" width={36} height={36} />
            <div>
              <div className="font-display font-semibold dark:text-frost">
                <span className="text-gradient">Omin</span>
                <span className="text-gradient-ia">IA</span> Pedidos
              </div>
              <div className="text-xs uppercase tracking-[0.3em] text-ink/45 dark:text-frost-muted">pedidos online</div>
            </div>
          </div>
          <div className="text-sm text-ink/55 dark:text-frost-muted">
            Dúvidas comerciais ou de implantação? Fale com a equipe que acompanha o seu cadastro.
          </div>
        </footer>
      </div>

      <style>{`
        @keyframes ominia-spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
